import React, { useState, useEffect } from 'react';
import { Box, SpaceBetween, Button, Input, Select, Multiselect, DatePicker, FormField, Modal, ColumnLayout, Autosuggest } from '@amzn/awsui-components-react';
import CustomMultiHybridInputAndSelect from '../Templates/components/CustomMultiHybridInputAndSelect';
import { APPROVER_TYPES, PROCESS_KEYS, UPLOADER_TYPES } from '../Frisco/Constants';
import { useSuggestedLogins } from 'src/hooks/useSuggestedLogins';
import { getDocumentBlocks } from './DocumentStoreAPIs';

const NewSectionModal = ({
    visible,
    onDismiss,
    onSave,
    onDelete,
    editData,
    getPartOptions = () => [],
    getDocumentOptions = () => [],
    allBlocks = [],
    getTieoutOptions = () => [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }],
    getDeliverableOptions = () => [],
    deliverablesLoading = false,
}) => {
    const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

    const typeOptions = {
        [PROCESS_KEYS.uploaders]: { values: [{ label: "USER", value: UPLOADER_TYPES.UPLOADER }, { label: "LDAP", value: UPLOADER_TYPES.UPLOADER_LDAP }] },
        [PROCESS_KEYS.approvers]: { values: [{ label: "USER", value: APPROVER_TYPES.APPROVER }, { label: "LDAP", value: APPROVER_TYPES.APPROVER_LDAP }] },
    };
    const getSelectFieldValues = (fieldName) => typeOptions[fieldName]?.values || [];
    const getInitialForm = (data) => {
        if (!data) return {
            sectionName: '', part: '', page: '', document: null,
            tieout: null, preWbDueDate: '', postWbDueDate: '',
            deliverables: [],
            [PROCESS_KEYS.uploaders]: [],
            [PROCESS_KEYS.approvers]: [],
        };
        const mappedDeliverables = (data.service_mappings || []).map(sm => {
            const key = `${sm.association_type}_${sm.association_id}`;
            const matchingOption = getDeliverableOptions().find(o => o.value === key);
            return matchingOption || { label: key, value: key };
        });
        return {
            sectionName: data.title || '',
            part: data.part_name || '',
            page: data.page || '',
            document: (() => {
                if (!data.association_id) return null;
                const docOpts = getDocumentOptions();
                if (data.block_document_id) {
                    const match = docOpts.find(o => String(o.value) === String(data.block_document_id));
                    if (match) return match;
                }
                if (docOpts.length === 1) return docOpts[0];
                return docOpts[0] || null;
            })(),
            tieout: data.tieout ? { label: data.tieout, value: data.tieout } : null,
            preWbDueDate: data.pre_wb_due_date || '',
            postWbDueDate: data.post_wb_due_date || '',
            deliverables: mappedDeliverables,
            [PROCESS_KEYS.uploaders]: (typeof data.preparers === 'string' ? JSON.parse(data.preparers) : (data.preparers || [])).map(p => ({ alias: p.alias, type: p.type })),
            [PROCESS_KEYS.approvers]: (typeof data.reviewers === 'string' ? JSON.parse(data.reviewers) : (data.reviewers || [])).map(r => ({ alias: r.alias, type: r.type })),
        };
    };

    const [form, setForm] = useState(getInitialForm(editData));

    const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
    const getValue = (fieldName) => form[fieldName];

    useEffect(() => {
        setForm(getInitialForm(editData));
    }, [editData]);

    // Deliverable options already include references in label from parent
    // No re-resolution needed since modal mounts fresh each time

    const [blocks, setBlocks] = useState([]);
    const docLookup = Object.fromEntries(getDocumentOptions().map(d => [d.value, d.label]));
    const selectedDocIds = form.document ? [form.document.value] : [];

    useEffect(() => {
        const fetchBlocks = async () => {
            if (selectedDocIds.length === 0) {
                setBlocks([]);
                return;
            }
            try {
                const result = await getDocumentBlocks({ documentIds: selectedDocIds, blockType: 'PART' });
                setBlocks(result || []);
            } catch (e) {
                console.error('Failed to fetch blocks', e);
            }
        };
        fetchBlocks();
    }, [JSON.stringify(selectedDocIds)]);

    const filteredPartOptions = blocks.map(b => ({
        label: b.block_name,
        value: b.block_name,
        description: docLookup[String(b.document_id)] || '',
    }));
    const postProcessTeamEmails = (value) => value.replaceAll("@amazon.com", "");
    const approversValidation = (elements) => {
        let valid = true;
        elements?.forEach((el) => { if (!el.type || !el.alias) valid = false; });
        return valid;
    };

    const updateValue = (fieldName, value, subKey = null, filterCriteria = null) => {
        setForm(prev => ({ ...prev, [fieldName]: value }));
    };

    const resetAndClose = () => {
        setForm({
            sectionName: '', part: '', page: '', document: null,
            tieout: null, preWbDueDate: '', postWbDueDate: '',
            deliverables: [],
            [PROCESS_KEYS.uploaders]: [],
            [PROCESS_KEYS.approvers]: [],
        });
        onDismiss();
    };

    return (
        <Modal visible={visible} onDismiss={resetAndClose} header={editData ? "Edit Section" : "New Section"} size="medium"
            footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
                {editData && onDelete && <Button variant="normal" onClick={() => { onDelete?.(editData); resetAndClose(); }}>Delete Section</Button>}
                <Button variant="link" onClick={resetAndClose}>Cancel</Button>
                {(form.sectionName && form.document && form.part) && <Button variant="primary" onClick={() => { onSave?.(form); resetAndClose(); }}>Save</Button>}
            </SpaceBetween></Box>}
        >
            {visible && <SpaceBetween size="l">
                <FormField label={<>Section Name <span className="mandatory-star">*</span></>}>
                    <Input value={form.sectionName} onChange={({ detail }) => update('sectionName', detail.value)} placeholder="Insert section name" />
                </FormField>
                <FormField label={<>Document <span className="mandatory-star">*</span></>}>
                    <Select selectedOption={form.document} onChange={({ detail }) => update('document', detail.selectedOption)} options={getDocumentOptions()} placeholder="Select document" filteringType="auto" />
                </FormField>
                <FormField label={<>Part <span className="mandatory-star">*</span></>}>
                    <Autosuggest value={form.part || ''} onChange={({ detail }) => update('part', detail.value)} options={filteredPartOptions} placeholder="Select or type part" enteredTextLabel={value => `Use: "${value}"`} empty={selectedDocIds.length === 0 ? "Select a document first" : "No matching part"} />
                </FormField>
                <FormField label="Page">
                    <Input value={form.page} onChange={({ detail }) => update('page', detail.value)} placeholder="Insert Page Number" type="number" inputMode="numeric" />
                </FormField>
                <FormField label="Preparer">
                    <CustomMultiHybridInputAndSelect key={`uploaders-${editData?.mapping_id || 'new'}`} field={PROCESS_KEYS.uploaders}
                        inputKey={PROCESS_KEYS.UPLOADER_KEYS.alias} selectKey={PROCESS_KEYS.UPLOADER_KEYS.type} selectHint="Choose type"
                        placeholder="alias" getValues={getValue} postProcess={postProcessTeamEmails} validator={approversValidation}
                        updateValueWithFilterCriteria={updateValue} getOptions={getSelectFieldValues} info=' ' errorMessage='type and alias input are mandatory' duplicatesNotAllowed={true}
                        loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} defaultSelectValue={UPLOADER_TYPES.UPLOADER} />
                </FormField>
                <FormField label="Reviewer">
                    <CustomMultiHybridInputAndSelect key={`approvers-${editData?.mapping_id || 'new'}`} field={PROCESS_KEYS.approvers}
                        inputKey={PROCESS_KEYS.APPROVER_KEYS.alias} selectKey={PROCESS_KEYS.APPROVER_KEYS.type} selectHint="Choose type"
                        placeholder="alias" getValues={getValue} postProcess={postProcessTeamEmails} validator={approversValidation}
                        updateValueWithFilterCriteria={updateValue} getOptions={getSelectFieldValues} info=' ' errorMessage='type and alias input are mandatory' duplicatesNotAllowed={true}
                        loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} defaultSelectValue={APPROVER_TYPES.APPROVER} />
                </FormField>
                <FormField label="Tie-out Needed">
                    <Select selectedOption={form.tieout} onChange={({ detail }) => update('tieout', detail.selectedOption)} options={getTieoutOptions()} placeholder="Yes/No" />
                </FormField>
                <ColumnLayout columns={2}>
                    <FormField label="Pre-WB Due Date">
                        <DatePicker value={form.preWbDueDate} onChange={({ detail }) => update('preWbDueDate', detail.value)} placeholder="Date" />
                    </FormField>
                    <FormField label="Post-WB Due Date">
                        <DatePicker value={form.postWbDueDate} onChange={({ detail }) => update('postWbDueDate', detail.value)} placeholder="Date" />
                    </FormField>
                </ColumnLayout>
                <FormField label="SEC Central Deliverables">
                    {deliverablesLoading
                        ? <Box color="text-status-inactive">Loading deliverables...</Box>
                        : <Multiselect selectedOptions={form.deliverables} onChange={({ detail }) => update('deliverables', detail.selectedOptions)} options={getDeliverableOptions()} placeholder="Map to SEC Central Deliverables References" filteringType="auto" keepOpen />
                    }
                </FormField>
            </SpaceBetween>}
        </Modal>
    );
};

export default NewSectionModal;
